import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[var(--background)]">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link
          href="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        <h1 className="text-3xl font-bold text-foreground mb-2">
          Privacy Policy
        </h1>
        <p className="text-muted-foreground mb-8">
          Last updated: February 10, 2026
        </p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Controller</h2>
            <p>
              habitus.net GmbH ("we", "us") is the controller of your personal
              data. Registered address: Zurich, Switzerland. Contact:
              privacy@habitus.net
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Data We Collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Account data:</strong> Email address, name,
                authentication provider (Google or email/password).
              </li>
              <li>
                <strong>Company data:</strong> Company name, UID (CHE number),
                address, bank account details, vendor and customer information
                as uploaded by you.
              </li>
              <li>
                <strong>Financial documents:</strong> Bank statements, invoices,
                contracts, and other documents you upload for processing.
              </li>
              <li>
                <strong>Transaction data:</strong> Financial transactions
                extracted from your uploaded documents.
              </li>
              <li>
                <strong>Usage data:</strong> Pages visited, features used,
                session identifiers, and analytics events for service
                improvement.
              </li>
              <li>
                <strong>Payment data:</strong> Processed by Stripe Inc. We store
                your Stripe customer ID and subscription status, but never your
                credit card details.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              3. Purpose and Legal Basis
            </h2>
            <p>We process your data for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Service delivery</strong> (Art. 6(1)(b) GDPR / Art.
                31(1) nDSG): Providing tax management, document processing, and
                financial analysis.
              </li>
              <li>
                <strong>AI-powered extraction</strong> (Art. 6(1)(b) GDPR):
                Using Google Vertex AI (Zurich region) to extract entities from
                your uploaded documents.
              </li>
              <li>
                <strong>Billing</strong> (Art. 6(1)(b) GDPR): Managing your
                subscription via Stripe.
              </li>
              <li>
                <strong>Analytics</strong> (Art. 6(1)(f) GDPR): Conversion
                funnel tracking to improve the service.
              </li>
              <li>
                <strong>Legal compliance</strong> (Art. 6(1)(c) GDPR): Meeting
                Swiss financial regulation requirements.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Data Residency</h2>
            <p>
              Your data is stored and processed exclusively within the European
              Economic Area and Switzerland:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Application hosting:</strong> Firebase (europe-west6,
                Zurich, Switzerland)
              </li>
              <li>
                <strong>Database:</strong> Neon PostgreSQL (eu-central-1,
                Frankfurt, Germany)
              </li>
              <li>
                <strong>AI processing:</strong> Google Vertex AI (europe-west6,
                Zurich, Switzerland)
              </li>
              <li>
                <strong>File storage:</strong> Firebase Storage (europe-west6,
                Zurich, Switzerland)
              </li>
              <li>
                <strong>Payment processing:</strong> Stripe Inc. operates
                servers in the EU. See{" "}
                <a
                  href="https://stripe.com/privacy"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Stripe Privacy Policy
                </a>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Data Sharing</h2>
            <p>We share your data only with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Google Cloud / Firebase:</strong> Hosting,
                authentication, AI processing (Data Processing Agreement in
                place).
              </li>
              <li>
                <strong>Neon Inc.:</strong> Database hosting (EU region, Data
                Processing Agreement in place).
              </li>
              <li>
                <strong>Stripe Inc.:</strong> Payment processing.
              </li>
            </ul>
            <p>We do not sell your data to third parties.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Retention</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Account data:</strong> Retained for the duration of your
                account. Deleted within 30 days of account deletion.
              </li>
              <li>
                <strong>Financial data:</strong> Retained for 10 years per Swiss
                commercial bookkeeping requirements (Art. 958f OR).
              </li>
              <li>
                <strong>Usage analytics:</strong> Retained for 24 months, then
                anonymized.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Your Rights</h2>
            <p>Under GDPR and the Swiss nDSG, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access your personal data</li>
              <li>Rectify inaccurate data</li>
              <li>
                Delete your data (subject to legal retention requirements)
              </li>
              <li>Restrict or object to processing</li>
              <li>Data portability</li>
              <li>
                Lodge a complaint with the FDPIC (Swiss Federal Data Protection
                Commissioner) or your local supervisory authority
              </li>
            </ul>
            <p>To exercise these rights, contact privacy@habitus.net.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Cookies</h2>
            <p>
              We use only essential cookies for authentication and session
              management. We do not use advertising or tracking cookies.
              Analytics events are tracked server-side without third-party
              cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Security</h2>
            <p>
              We implement industry-standard security measures including
              encryption in transit (TLS 1.3), encryption at rest, role-based
              access controls, and regular security reviews. Financial data is
              processed in FINMA-aware infrastructure with appropriate data
              classification.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Changes</h2>
            <p>
              We may update this policy from time to time. Material changes will
              be communicated via email or in-app notification at least 30 days
              before taking effect.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
